import React from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import ReactRouterPrompt from 'react-router-prompt';
import { preventEnterOnSubmit } from 'src/pages/helpers';
import { warehouseIncomeSchema as validationSchema } from './validationSchema';
import {
  WarehouseIncomeValues,
  WarehouseIncomeType,
} from '../WarehouseIncomesPage/services';
import {
  BackButton,
  SubmitButton,
} from 'src/components/MainPage/_shared/forms';
import { ConfirmDialog } from 'src/components/MainPage/_shared/ConfirmDialog';
import { CustomCard } from 'src/components/MainPage/_shared/CustomCard';
import { useWarehouseIncomesData } from '../WarehouseIncomesPage/hooks/useWarehouseIncomesData';
import { WAREHOUSE_INCOMES_ROOT } from './WarehouseIncomePage';
import { ItemsEditableTable } from './components/ItemsEditableTable';

type WarehouseIncomeFormProps = {
  initialValues: WarehouseIncomeValues;
  isMutated: boolean;
  onSubmit: (values: WarehouseIncomeValues) => void;
  isReadOnly?: boolean;
};

export const WarehouseIncomeForm = ({
  initialValues,
  isMutated,
  onSubmit,
  isReadOnly = false,
}: WarehouseIncomeFormProps) => {
  const { types, warehouses, suppliers } = useWarehouseIncomesData();

  const formik = useFormik({
    initialValues: structuredClone(initialValues),
    validationSchema,
    onSubmit,
  });

  const { values, touched, errors, dirty, isSubmitting, setFieldValue } =
    formik;

  const selectedWarehouse =
    warehouses?.find((w) => w.id === values.warehouseId) ?? null;
  const selectedSupplier =
    suppliers?.find((s) => s.id === values.supplierId) ?? null;
  const selectedType = types?.find((t) => t.id === values.type) ?? null;

  return (
    <FormikProvider value={formik}>
      <Form noValidate onKeyDown={preventEnterOnSubmit}>
        <Stack gap={3} pb={2}>
          <CustomCard
            header={<Typography p={2}>Данные о приемке</Typography>}
            body={
              <Stack p={2} gap={2}>
                <Stack gap={2} sx={{ flexDirection: { md: 'row' } }}>
                  <Autocomplete
                    id="type"
                    options={types ?? []}
                    value={selectedType}
                    onChange={(_, newValue) =>
                      setFieldValue(
                        'type',
                        (newValue?.id as WarehouseIncomeType) ?? null
                      )
                    }
                    getOptionLabel={(option) => option.description}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Тип акта"
                        required
                        error={touched.type && !!errors.type}
                        helperText={(touched.type && errors.type) || undefined}
                      />
                    )}
                    disabled={isReadOnly}
                    fullWidth
                    noOptionsText="Не найдено"
                  />

                  <DateTimePicker
                    ampm={false}
                    inputFormat="dd.MM.yyyy HH:mm"
                    value={values.documentDateTime}
                    onChange={(newValue) =>
                      setFieldValue('documentDateTime', newValue)
                    }
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        id="documentDateTime"
                        name="documentDateTime"
                        label="Дата и время приёмки"
                        fullWidth
                        required
                        error={
                          touched.documentDateTime && !!errors.documentDateTime
                        }
                        helperText={
                          (touched.documentDateTime &&
                            errors.documentDateTime) ||
                          undefined
                        }
                      />
                    )}
                    disabled={isReadOnly}
                  />
                </Stack>

                <Stack gap={2} sx={{ flexDirection: { md: 'row' } }}>
                  <Autocomplete
                    id="warehouseId"
                    options={warehouses ?? []}
                    value={selectedWarehouse}
                    onChange={(_, newValue) =>
                      setFieldValue('warehouseId', newValue?.id ?? null)
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Склад"
                        required
                        error={touched.warehouseId && !!errors.warehouseId}
                        helperText={
                          (touched.warehouseId && errors.warehouseId) ||
                          undefined
                        }
                      />
                    )}
                    disabled={isReadOnly}
                    fullWidth
                    noOptionsText="Не найдено"
                  />
                  <Autocomplete
                    id="supplierId"
                    options={suppliers ?? []}
                    value={selectedSupplier}
                    onChange={(_, newValue) =>
                      setFieldValue('supplierId', newValue?.id ?? null)
                    }
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Поставщик" />
                    )}
                    disabled={isReadOnly}
                    fullWidth
                    noOptionsText="Не найдено"
                  />
                </Stack>

                <Stack gap={2} sx={{ flexDirection: { md: 'row' } }}>
                  <Field
                    id="comment"
                    name="comment"
                    label="Комментарий"
                    as={TextField}
                    fullWidth
                    rows={2}
                    disabled={isReadOnly}
                  />
                </Stack>
              </Stack>
            }
          />

          <CustomCard
            header={<Typography p={2}>Номенклатуры</Typography>}
            body={<ItemsEditableTable disabled={isReadOnly} />}
          />

          <Stack direction="row" spacing={2}>
            {!isReadOnly && (
              <SubmitButton
                loading={isSubmitting}
                disabled={isSubmitting || !dirty}
              />
            )}
            <BackButton
              disabled={isSubmitting}
              backTo={WAREHOUSE_INCOMES_ROOT}
            />
          </Stack>
        </Stack>
      </Form>

      <ReactRouterPrompt when={!isMutated && dirty}>
        {({ isActive, onConfirm, onCancel }) => (
          <ConfirmDialog
            isOpen={isActive}
            onClose={onCancel}
            onConfirm={onConfirm}
          />
        )}
      </ReactRouterPrompt>
    </FormikProvider>
  );
};
